/**
 * @format
 */

import React, { useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Autocomplete,
  Stack,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  ListItemText,
  Checkbox,
  MenuItem,
  Chip,
} from '@mui/material';

import {
  Visibility,
  VisibilityOff,
  LockReset,
  Save,
  Person,
  Security,
} from '@mui/icons-material';

import inputStyle from '../styles/styles';
import { useUserEditForm } from '../hooks/useUserEditForm';
import { fieldSx, selectMenuProps } from '../../Team/TeamPage';

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 1 }}>
    {value === index && <Box>{children}</Box>}
  </Box>
);

const UserEditForm = ({ userId, formData, setFormData, onSuccess }) => {
  const {
    tabIndex,
    setTabIndex,
    loading,
    resetOpen,
    setResetOpen,
    newPassword,
    setNewPassword,
    showPassword,
    setShowPassword,
    roles,
    userRoles,
    roleLoading,
    handleUpdateUser,
    handleResetPassword,
    handleUpdateRole,
    handleProfileUpdate,
    jobTitles,
    setJobTitles,
    globalRoles,
    setGlobalRoles,
    skills,
    setSkills,
    expertise,
    setExpertise,
  } = useUserEditForm(userId, formData, setFormData, onSuccess);

  const globalRolesDraftRef = useRef(globalRoles.selected);

  useEffect(() => {
    globalRolesDraftRef.current = globalRoles.selected;
  }, [globalRoles.selected]);

  return (
    <Paper
      sx={{
        p: 1.5,
        borderRadius: 2,
        background: 'rgba(255,255,255,0.05)',
        color: '#fff',
      }}
    >
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        variant="fullWidth"
        sx={{
          mb: 1.5,
          minHeight: 30,
          '& .MuiTab-root': {
            minHeight: 30,
            fontSize: 13,
            fontWeight: 600,
            color: '#aaa',
          },
          '& .Mui-selected': {
            color: '#fff',
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#101112',
          },
        }}
      >
        <Tab
          icon={<Person sx={{ fontSize: 18, color: '#fff' }} />}
          label="Details"
        />
        <Tab
          icon={<Security sx={{ fontSize: 18, color: '#fff' }} />}
          label="Roles & Jobs"
        />
        <Tab
          icon={<LockReset sx={{ fontSize: 18, color: '#fff' }} />}
          label="Password"
        />
      </Tabs>

      <TabPanel value={tabIndex} index={0}>
        <Stack spacing={1.5} component="form" onSubmit={handleUpdateUser}>
          <TextField
            label="Username"
            value={formData.username}
            disabled
            size="small"
            sx={inputStyle}
          />
          <TextField
            label="First Name"
            size="small"
            value={formData.firstName}
            onChange={(e) =>
              setFormData({ ...formData, firstName: e.target.value })
            }
            sx={inputStyle}
          />
          <TextField
            label="Last Name"
            size="small"
            value={formData.lastName}
            onChange={(e) =>
              setFormData({ ...formData, lastName: e.target.value })
            }
            sx={inputStyle}
          />
          <TextField
            label="Email"
            size="small"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            sx={inputStyle}
          />

          <Autocomplete
            size="small"
            options={roles}
            getOptionLabel={(option) => option.name}
            value={userRoles[0] || null}
            onChange={handleUpdateRole}
            loading={roleLoading}
            PaperComponent={(props) => (
              <Paper
                {...props}
                sx={{
                  background: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#fff',
                }}
              />
            )}
            sx={{
              '& .MuiInputBase-root': {
                color: '#fff',
                fontSize: 14,
                background: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(10px)',
              },
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select Role" sx={inputStyle} />
            )}
          />

          <Button
            size="small"
            type="submit"
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={14} />
              ) : (
                <Save sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              width: '160px',
              alignSelf: 'center',
              fontSize: 13,
              textTransform: 'none',
              borderRadius: 1.5,
              background: '#9c27b0',
              color: '#fff',
              '&:hover': {
                background: '#7b1fa2',
              },
            }}
          >
            Save
          </Button>
        </Stack>
      </TabPanel>

      <TabPanel value={tabIndex} index={1}>
        <FormControl fullWidth sx={fieldSx}>
          <InputLabel id="role-job-title-label">Job Titles</InputLabel>
          <Select
            labelId="role-job-title-label"
            multiple
            value={jobTitles.selected}
            onChange={(event) =>
              setJobTitles((prev) => ({
                ...prev,
                selected: event.target.value,
              }))
            }
            onClose={() => {
              handleProfileUpdate(
                'jobTitles',
                jobTitles.selected,
                'Job titles updated'
              );
            }}
            input={<OutlinedInput label="Job Titles" />}
            renderValue={(selected) => (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {selected.map((id) => {
                  const jt = jobTitles.options.find((j) => j.jobTitleId === id);
                  return (
                    <Chip
                      key={id}
                      label={jt?.jobTitleName}
                      size="small"
                      sx={{ bgcolor: 'rgba(215, 212, 212, 0.2)' }}
                    />
                  );
                })}
              </Stack>
            )}
            MenuProps={selectMenuProps}
          >
            {jobTitles.options.map((jt) => (
              <MenuItem key={jt.jobTitleId} value={jt.jobTitleId}>
                <Checkbox
                  checked={jobTitles.selected.includes(jt.jobTitleId)}
                />
                <ListItemText primary={jt.jobTitleName} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ ...fieldSx, mt: 2 }}>
          <InputLabel id="role-global-role-label">Global Role</InputLabel>
          <Select
            labelId="role-global-role-label"
            multiple
            value={globalRoles.selected}
            onChange={(event) => {
              const selected = event.target.value;
              globalRolesDraftRef.current = selected;
              setGlobalRoles((prev) => ({
                ...prev,
                selected,
              }));
            }}
            onClose={() => {
              handleProfileUpdate(
                'globalRoles',
                globalRolesDraftRef.current,
                'Global roles updated'
              );
            }}
            input={<OutlinedInput label="Global Role" />}
            renderValue={(selected) => (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {selected.map((id) => {
                  const role = globalRoles.options.find((r) => r.roleId === id);
                  return (
                    <Chip
                      key={id}
                      label={role?.roleName}
                      size="small"
                      sx={{ bgcolor: 'rgba(215, 212, 212, 0.2)' }}
                    />
                  );
                })}
              </Stack>
            )}
            MenuProps={selectMenuProps}
          >
            {globalRoles.options.map((role) => (
              <MenuItem key={role.roleId} value={role.roleId}>
                <Checkbox
                  checked={globalRoles.selected.includes(role.roleId)}
                />
                <ListItemText primary={role.roleName} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ ...fieldSx, mt: 2 }}>
          <InputLabel id="role-skills-label">Skills</InputLabel>
          <Select
            labelId="role-skills-label"
            multiple
            value={skills.selected}
            onChange={(event) =>
              setSkills((prev) => ({
                ...prev,
                selected: event.target.value,
              }))
            }
            onClose={() => {
              const val = skills.selected.map((id) => {
                return skills.options.find((skill) => skill.skillId === id)
                  ?.skillName;
              });
              handleProfileUpdate('skills', val, 'Skills updated');
            }}
            input={<OutlinedInput label="Skills" />}
            renderValue={(selected) => (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {selected.map((id) => {
                  const skill = skills.options.find((s) => s.skillId === id);
                  return (
                    <Chip
                      key={id}
                      label={skill?.skillName}
                      size="small"
                      sx={{ bgcolor: 'rgba(215, 212, 212, 0.2)' }}
                    />
                  );
                })}
              </Stack>
            )}
            MenuProps={selectMenuProps}
          >
            {skills.options.map((skill) => (
              <MenuItem key={skill.skillId} value={skill.skillId}>
                <Checkbox checked={skills.selected.includes(skill.skillId)} />
                <ListItemText primary={skill.skillName} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ ...fieldSx, mt: 2 }}>
          <InputLabel id="role-expertise-label">Expertise</InputLabel>
          <Select
            labelId="role-expertise-label"
            multiple
            value={expertise.selected}
            onChange={(event) =>
              setExpertise((prev) => ({
                ...prev,
                selected: event.target.value,
              }))
            }
            onClose={() => {
              const val = expertise.selected.map((id) => {
                return expertise.options.find((exp) => exp.expertiseId === id)
                  ?.expertiseName;
              });
              handleProfileUpdate('expertise', val, 'Expertise updated');
            }}
            input={<OutlinedInput label="Expertise" />}
            renderValue={(selected) => (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {selected.map((id) => {
                  const exp = expertise.options.find(
                    (e) => e.expertiseId === id
                  );
                  return (
                    <Chip
                      key={id}
                      label={exp?.expertiseName}
                      size="small"
                      sx={{ bgcolor: 'rgba(215, 212, 212, 0.2)' }}
                    />
                  );
                })}
              </Stack>
            )}
            MenuProps={selectMenuProps}
          >
            {expertise.options.map((exp) => (
              <MenuItem key={exp.expertiseId} value={exp.expertiseId}>
                <Checkbox
                  checked={expertise.selected.includes(exp.expertiseId)}
                />
                <ListItemText primary={exp.expertiseName} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TabPanel>

      <TabPanel value={tabIndex} index={2}>
        <Stack alignItems="center">
          <Button
            variant="outlined"
            size="small"
            sx={{
              fontSize: 13,
              textTransform: 'none',
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.4)',
            }}
            onClick={() => setResetOpen(true)}
          >
            Reset Password
          </Button>
        </Stack>
      </TabPanel>

      <Dialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        maxWidth="xs"
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(8px)',
          },
        }}
        PaperProps={{
          sx: {
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 2,
            color: '#fff',
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 16 }}>Reset Password</DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <TextField
            fullWidth
            size="small"
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={inputStyle}
            placeholder="Enter new password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? (
                      <VisibilityOff sx={{ color: '#fff' }} />
                    ) : (
                      <Visibility sx={{ color: '#fff' }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => setResetOpen(false)}
            variant="outlined"
            sx={{
              color: '#fff',
              fontSize: 13,
              borderColor: 'rgba(255,255,255,0.3)',
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleResetPassword}
            sx={{
              fontSize: 13,
              background: '#9c27b0',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                background: '#7b1fa2',
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UserEditForm;
